/**
 * Reseller Invitations tab (TMMPDM-0015) — 0004's InvitationsPanel shape: PENDING by default
 * with a "Show all" switch; per PENDING row Resend and Revoke (confirm). `status` is
 * server-computed and rendered as returned. A 409 `INVITATION_ALREADY_ACCEPTED` or a 404
 * shows its message and refetches. `refreshKey` is bumped by the Users tab after an invite.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState } from '@/components/page';
import { PeopleInvitationsBlock } from '../partners/PeopleInvitationsBlock';
import { PeopleTableContainer } from '../partners/peopleLeafLayout';
import {
  listResellerInvitations,
  resendResellerInvitation,
  revokeResellerInvitation,
} from '@/api/saas/resellers';
import { ApiError, errorCopy } from '@/api/errors';
import type { ResellerInvitationDto } from '@/api/saas/types';
import { formatInstant } from '@/format';
import { StatusChip } from '@/status';

interface ResellerInvitationsPanelProps {
  resellerId: number;
  refreshKey?: number;
}

export function ResellerInvitationsPanel({
  resellerId,
  refreshKey = 0,
}: ResellerInvitationsPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${resellerId}:${showAll}:${reloadToken}:${refreshKey}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    invitations: ResellerInvitationDto[] | null;
    failure: unknown;
  }>({ requestKey: null, invitations: null, failure: null });
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ResellerInvitationDto | null>(null);
  // A refused revoke is said inside its confirm, which stays open (TMM-B4).
  const [revokeError, setRevokeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listResellerInvitations(resellerId, showAll ? undefined : 'PENDING')
      .then((invitations) => {
        if (!cancelled) setResponse({ requestKey, invitations, failure: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setResponse({ requestKey, invitations: null, failure: err });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, showAll, requestKey]);

  const current = response.requestKey === requestKey;
  const invitations = current ? response.invitations : null;
  const refetch = () => setReloadToken((n) => n + 1);

  /** True when the write went through. */
  const run = async (
    invitation: ResellerInvitationDto,
    action: 'resend' | 'revoke',
  ): Promise<boolean> => {
    const report = action === 'revoke' ? setRevokeError : setActionError;
    setActionError(null);
    setRevokeError(null);
    setBusyId(invitation.id);
    try {
      if (action === 'resend') await resendResellerInvitation(resellerId, invitation.id);
      else await revokeResellerInvitation(resellerId, invitation.id);
      refetch();
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        report(
          t(
            'tenantDetail.invitations.notFound',
            'This invitation could not be found. The list has been refreshed.',
          ),
        );
        refetch();
      } else {
        report(
          errorCopy(
            err,
            t,
            action === 'resend'
              ? t(
                  'tenantDetail.invitations.resendError',
                  'Could not resend invitation. Please try again.',
                )
              : t(
                  'tenantDetail.invitations.revokeError',
                  'Could not revoke invitation. Please try again.',
                ),
          ),
        );
        if (err instanceof ApiError && err.code === 'INVITATION_ALREADY_ACCEPTED') refetch();
      }
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const ready = current && invitations !== null;

  return (
    <Box>
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {current && invitations === null ? (
        <ErrorState
          error={response.failure}
          message={t('tenantDetail.invitations.loadError', 'Could not load invitations.')}
          onRetry={refetch}
        />
      ) : (
        <PeopleInvitationsBlock
          ready={ready}
          count={invitations?.length ?? 0}
          showAll={showAll}
          onShowAll={setShowAll}
          headingId="reseller-invitations"
        >
          {invitations ? (
            <PeopleTableContainer>
              <Table
                aria-label={t('resellers.invitations.tableAriaLabel', 'Reseller invitations')}
                size="small"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tenantDetail.invitations.col.email', 'Email')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.role', 'Role')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.status', 'Status')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.created', 'Created')}</TableCell>
                    <TableCell>{t('tenantDetail.invitations.col.expires', 'Expires')}</TableCell>
                    <TableCell align="right">
                      {t('tenantDetail.invitations.col.actions', 'Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <EmptyState
                          title={
                            showAll
                              ? t('tenantDetail.invitations.emptyAll', 'No invitations.')
                              : t('tenantDetail.invitations.empty', 'No invitations pending.')
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    invitations.map((invitation) => {
                      const busy = busyId === invitation.id;
                      return (
                        <TableRow key={invitation.id}>
                          <TableCell>{invitation.email}</TableCell>
                          <TableCell>
                            {t(`resellers.role.${invitation.role}`, invitation.role)}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              status={invitation.status}
                              domain="invitation"
                              label={t(
                                `tenantDetail.invitations.status.${invitation.status}`,
                                invitation.status,
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            {formatInstant(invitation.createdAt, i18n.language)}
                          </TableCell>
                          <TableCell>
                            {formatInstant(invitation.expiresAt, i18n.language)}
                          </TableCell>
                          <TableCell align="right">
                            {invitation.status === 'PENDING' ? (
                              <>
                                <Button
                                  size="small"
                                  disabled={busy}
                                  onClick={() => void run(invitation, 'resend')}
                                >
                                  {t('tenantDetail.invitations.resendButton', 'Resend')}
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  disabled={busy}
                                  onClick={() => setRevokeTarget(invitation)}
                                >
                                  {t('tenantDetail.invitations.revokeButton', 'Revoke')}
                                </Button>
                              </>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </PeopleTableContainer>
          ) : null}
        </PeopleInvitationsBlock>
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        title={t('tenantDetail.invitations.revokeConfirmTitle', 'Revoke invitation?')}
        consequence={t(
          'tenantDetail.invitations.revokeConfirmBody',
          'This revokes the pending invitation to {{email}}. They will no longer be able to accept it.',
          { email: revokeTarget?.email ?? '' },
        )}
        confirmLabel={t('tenantDetail.invitations.revokeButton', 'Revoke')}
        destructive
        busy={busyId !== null}
        error={revokeError}
        onConfirm={() => {
          const target = revokeTarget;
          if (target)
            void run(target, 'revoke').then((revoked) => {
              if (revoked) setRevokeTarget(null);
            });
        }}
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeError(null);
        }}
      />
    </Box>
  );
}
